import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import type { Session } from '@supabase/supabase-js';
import { supabase } from '../lib/supabaseClient';
import { fetchAdminMe } from '../lib/api';

// Autenticação do painel ADMINISTRATIVO — Supabase Auth (e-mail + senha,
// revisado em 2026-09-08 — antes era e-mail + código OTP; ver
// AdminLoginPage.tsx). Nada a ver com o acesso do influenciador (ver
// AffiliateSessionContext.tsx) — dois mecanismos completamente
// separados, sem nenhum código compartilhado.

interface AdminAuthState {
  loading: boolean;
  session: Session | null;
  isAdmin: boolean;
  adminCheckError: string | null;
  signOut: () => Promise<void>;
}

const AdminAuthContext = createContext<AdminAuthState | undefined>(undefined);

export function AdminAuthProvider({ children }: { children: ReactNode }) {
  const [loading, setLoading] = useState(true);
  const [session, setSession] = useState<Session | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [adminCheckError, setAdminCheckError] = useState<string | null>(null);

  async function checkAdminAccess() {
    try {
      await fetchAdminMe();
      setIsAdmin(true);
      setAdminCheckError(null);
    } catch (error) {
      setIsAdmin(false);
      setAdminCheckError(error instanceof Error ? error.message : 'Esta conta não tem acesso ao painel.');
    }
  }

  useEffect(() => {
    supabase.auth.getSession().then(async ({ data }) => {
      setSession(data.session);
      if (data.session) {
        await checkAdminAccess();
      }
      setLoading(false);
    });

    const { data: subscription } = supabase.auth.onAuthStateChange(async (_event, nextSession) => {
      setSession(nextSession);
      if (nextSession) {
        await checkAdminAccess();
      } else {
        setIsAdmin(false);
        setAdminCheckError(null);
      }
    });

    return () => subscription.subscription.unsubscribe();
  }, []);

  const value: AdminAuthState = {
    loading,
    session,
    isAdmin,
    adminCheckError,
    signOut: async () => {
      await supabase.auth.signOut();
    },
  };

  return <AdminAuthContext.Provider value={value}>{children}</AdminAuthContext.Provider>;
}

export function useAdminAuth(): AdminAuthState {
  const context = useContext(AdminAuthContext);
  if (!context) {
    throw new Error('useAdminAuth must be used within an AdminAuthProvider');
  }
  return context;
}
